import { useState, useEffect } from 'react';

// Define types for the predictions from the Google Places API
export interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings?: Array<{
      length: number;
      offset: number;
    }>;
  };
  matched_substrings?: Array<{
    length: number;
    offset: number;
  }>;
  terms?: Array<{
    offset: number;
    value: string;
  }>;
  types?: string[];
}

interface PlacesResponse {
  success: boolean;
  predictions: PlacePrediction[];
  error?: string;
}

/**
 * Helper function to extract the main address from structured_formatting
 */
export function getMainText(prediction: PlacePrediction | string): string {
  if (typeof prediction === 'string') return '';
  return prediction?.structured_formatting?.main_text || '';
}

/**
 * Helper function to extract the secondary part of the address
 */
export function getSecondaryText(prediction: PlacePrediction | string): string {
  if (typeof prediction === 'string') return '';
  return prediction?.structured_formatting?.secondary_text || '';
}

/**
 * Helper function to get the full address
 */
export function getFullAddress(prediction: PlacePrediction | string): string {
  if (typeof prediction === 'string') return prediction;
  return prediction?.description || '';
}

/**
 * Hook for fetching place suggestions from Google Places API
 * @param {string} input - The search input
 * @param {number} debounceMs - Debounce delay in milliseconds
 * @returns {Object} Places data, loading state, and error information
 */
export function usePlaces(input: string, debounceMs = 300) {
  const [data, setData] = useState<PlacePrediction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [debouncedInput, setDebouncedInput] = useState<string>(input);
  
  // Debounce input to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInput(input);
    }, debounceMs);
    
    return () => clearTimeout(timer);
  }, [input, debounceMs]);
  
  // Fetch places when debounced input changes
  useEffect(() => {
    // Don't make API call if input is empty or too short
    if (!debouncedInput || debouncedInput.length < 3) {
      setData([]);
      return;
    }
    
    const fetchPlaces = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`/api/places/complete?input=${encodeURIComponent(debouncedInput)}`);
        const result = await response.json() as PlacesResponse;
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to fetch places');
        }
        
        setData(result.predictions);
      } catch (err) {
        console.error('Error fetching place suggestions:', err);
        // Handle the unknown type
        const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching places';
        setError(errorMessage);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlaces();
  }, [debouncedInput]);
  
  return {
    suggestions: data,
    loading,
    error,
    getMainText,
    getSecondaryText,
    getFullAddress
  };
}